import { breeze, DataProperty, DataService, DataType, EntityManager, MetadataStore } from 'breeze-client';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { BusyService } from './busy.service';
import { LookupTypesCache } from './lookup-types-cache';
import { mapValueToServer, tryParseDate } from './map-json-server-object';

export type RegistrationHelper = (metadataStore: MetadataStore) => void;

export class EntityManagerProvider {
  readonly lookupTypes$: Observable<any>;

  protected readonly masterManager: EntityManager;
  protected readonly metadataStore: MetadataStore;

  constructor(
    metadata: Record<string, unknown> | MetadataStore,
    controller: string,
    busyService: BusyService,
    options?: {
      registrationHelper?: RegistrationHelper;
      lookupTypesCache?: LookupTypesCache;
    }
  ) {
    options = options || {};

    const dataService = new DataService({
      serviceName: `${environment.apiRoot}/${controller}`,
      hasServerMetadata: false,
    });

    if (metadata instanceof MetadataStore) {
      this.masterManager = new EntityManager({
        dataService,
        metadataStore: metadata,
      });
      this.metadataStore = metadata;
    } else {
      this.masterManager = new EntityManager({
        dataService,
      });
      this.metadataStore = this.masterManager.metadataStore;
      this.metadataStore.importMetadata(metadata);
      if (options.registrationHelper) {
        options.registrationHelper(this.metadataStore);
      }
    }

    // change Breeze date parsing and serialization to UTC
    breeze.DataType.parseDateFromServer = (source: any) => tryParseDate(source);
    this.metadataStore.setProperties({
      serializerFn: (dp: DataProperty, val) => {
        if (dp.isDataProperty && val && dp.dataType === DataType.DateTime) {
          return mapValueToServer(val);
        }

        return val;
      },
    });

    // We want to throw an error if NaN, Infinity or -Inifinity
    const throwIfNotFinite =
      (origParse: (source: any, sourceTypeName: string) => any): ((source: any, sourceTypeName: string) => any) =>
      (source: any, sourceTypeName: string) => {
        if (sourceTypeName === 'number' && !isFinite(source)) {
          throw new Error(`Possible division by zero detected. Attempting to set property to: ${source}`);
        }

        if (origParse) {
          return origParse(source, sourceTypeName);
        }

        return;
      };
    breeze.DataType.Decimal.parse = throwIfNotFinite(breeze.DataType.Decimal.parse);
    breeze.DataType.Double.parse = throwIfNotFinite(breeze.DataType.Decimal.parse);
    breeze.DataType.Single.parse = throwIfNotFinite(breeze.DataType.Decimal.parse);

    const lookupTypesCache =
      options.lookupTypesCache || new LookupTypesCache(this.metadataStore, controller, 'LookupTypes', busyService);
    this.lookupTypes$ = lookupTypesCache.lookupTypes$;
  }

  get serviceName(): string {
    return this.masterManager?.serviceName;
  }

  newManager(): EntityManager {
    return this.masterManager.createEmptyCopy();
  }
}
